import type {
  ThreadId,
  ScopedProjectRef,
  ClientOrchestrationCommand,
  EnvironmentApi,
  EnvironmentId,
} from "@cmmd-forge/contracts";
import { useCallback } from "react";

import { useComposerDraftStore } from "../composerDraftStore";
import { readEnvironmentApi } from "../environmentApi";
import { usePrimaryEnvironmentId } from "../environments/primary";
import { buildPrimaryFetchInit } from "../environments/primary/forgeSessionClient";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import {
  ensureProjectLeadTaskCreationThread,
  ProjectLeadThreadCreateRejectedError,
  type EnsureProjectLeadTaskCreationThreadInput,
  type EnsureProjectLeadTaskCreationThreadResult,
} from "../lib/projectLeadTaskCreationThread";
import { seedThreadComposerContext } from "../lib/threadComposerBootstrap";
import { newCommandId, newThreadId } from "../lib/utils";
import { selectSidebarThreadsForProjectRef, useStore } from "../store";
import { selectProjectByRef } from "../store";
import { useServerProviders } from "../rpc/serverState";
import { prepareThreadProviderLaunch } from "../components/NoActiveThreadState.threadRunner";
import { ensureTaskWorkTargetConnection } from "../components/tasks/taskWorkTargetConnection";
import { resolveTaskWorkTargetProjectRef } from "../components/tasks/taskWorkTargetConnection";
import { useCmmdWorkIntakeSnapshot } from "../cmmd/workIntakeResource";
import { cmmdApiClient } from "../environments/cmmdApi";
import { useSavedEnvironmentRegistryStore } from "../environments/runtime";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";

function requiredProjectLeadTargetContext(input: {
  readonly projectRef: EnsureProjectLeadTaskCreationThreadInput["projectRef"];
  readonly cmmdProjectId: string;
  readonly cmmdIntake: ReturnType<typeof useCmmdWorkIntakeSnapshot>;
}) {
  const cmmdProject = input.cmmdIntake.projects.find(
    (candidate) => String(candidate.id) === input.cmmdProjectId,
  );
  if (!cmmdProject) {
    throw new Error("The selected Forge project runtime is not available.");
  }
  const environmentId = String(input.projectRef.environmentId);
  const savedEnvironment =
    useSavedEnvironmentRegistryStore.getState().byId[input.projectRef.environmentId];
  const encodedCloudRuntimeId = classifyEnvironmentId(environmentId).legacyRuntimeId;
  const environment =
    savedEnvironment ??
    (encodedCloudRuntimeId
      ? {
          runtimeId: encodedCloudRuntimeId,
          label: cmmdProject.title,
          httpBaseUrl: null,
          lastConnectedAt: null,
        }
      : null);
  if (!environment) throw new Error("The selected Forge project runtime is not available.");
  return { cmmdProject, environment };
}

function runtimePresentation(environmentId: string): {
  readonly source: "forge-cloud" | "registered";
  readonly runtimeKind: "cloud" | "desktop";
} {
  return classifyEnvironmentId(environmentId).isCloud
    ? { source: "forge-cloud", runtimeKind: "cloud" }
    : { source: "registered", runtimeKind: "desktop" };
}

export async function ensureProjectLeadTarget(input: {
  readonly projectRef: EnsureProjectLeadTaskCreationThreadInput["projectRef"];
  readonly cmmdProjectId: string;
  readonly primaryEnvironmentId: ReturnType<typeof usePrimaryEnvironmentId>;
  readonly cmmdIntake: ReturnType<typeof useCmmdWorkIntakeSnapshot>;
  readonly connect?: typeof ensureTaskWorkTargetConnection;
}): Promise<EnsureProjectLeadTaskCreationThreadInput["projectRef"]> {
  if (input.projectRef.environmentId === input.primaryEnvironmentId) return input.projectRef;
  const { cmmdProject, environment } = requiredProjectLeadTargetContext(input);
  const forgeProject = selectProjectByRef(useStore.getState(), input.projectRef);
  const environmentKey = String(input.projectRef.environmentId);
  const runtimeId =
    environment.runtimeId ??
    classifyEnvironmentId(environmentKey).legacyRuntimeId ??
    environmentKey;
  const presentation = runtimePresentation(String(input.projectRef.environmentId));
  const target = {
    organizationId: Number(cmmdProject.workspaceId ?? 0),
    runtimeId,
    forgeProjectLinkId: 0,
    environmentId: input.projectRef.environmentId,
    projectId: input.projectRef.projectId,
    runtime: {
      id: runtimeId,
      source: presentation.source,
      runtimeKind: presentation.runtimeKind,
      displayName: environment.label,
      status: "unknown",
      endpointUrl: environment.httpBaseUrl,
      hostId: null,
      deviceId: null,
      projectId: Number(input.cmmdProjectId),
      projectName: cmmdProject.title,
      workspaceRoot: forgeProject?.cwd ?? null,
      githubRepoFullName: null,
      lastSeenAt: environment.lastConnectedAt,
      metadata: null,
    },
    workspaceRoot: forgeProject?.cwd ?? null,
    githubRepoFullName: null,
  } as const;
  await (input.connect ?? ensureTaskWorkTargetConnection)({
    target,
    project: cmmdProject,
    primaryEnvironmentId: input.primaryEnvironmentId,
  });
  return resolveTaskWorkTargetProjectRef({
    target,
    cmmdProjectId: input.cmmdProjectId,
    environmentStateById: useStore.getState().environmentStateById,
  });
}

type ThreadCreateCommand = Extract<ClientOrchestrationCommand, { readonly type: "thread.create" }>;
type ProjectLeadCreateCommand = ThreadCreateCommand;

const THREAD_CONFIRMATION_ATTEMPTS = 50;
const THREAD_CONFIRMATION_INTERVAL_MS = 100;
const THREAD_DURABILITY_ATTEMPTS = 40;
const THREAD_DURABILITY_INTERVAL_MS = 250;

export async function waitForProjectLeadThreadProjection(input: {
  readonly projectRef: ScopedProjectRef;
  readonly threadId: ThreadId;
  readonly findThreads?: typeof selectSidebarThreadsForProjectRef;
  readonly readState?: typeof useStore.getState;
  readonly wait?: (durationMs: number) => Promise<void>;
  readonly attempts?: number;
}): Promise<void> {
  const findThreads = input.findThreads ?? selectSidebarThreadsForProjectRef;
  const readState = input.readState ?? useStore.getState;
  const wait =
    input.wait ??
    ((durationMs: number) => new Promise((resolve) => setTimeout(resolve, durationMs)));
  const attempts = input.attempts ?? THREAD_CONFIRMATION_ATTEMPTS;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (findThreads(readState(), input.projectRef).some((thread) => thread.id === input.threadId)) {
      return;
    }
    if (attempt + 1 < attempts) await wait(THREAD_CONFIRMATION_INTERVAL_MS);
  }
  throw new Error(
    "Forge accepted the Project Lead conversation but did not confirm it. Retry to continue safely.",
  );
}

function findProjectLeadThreadReceipt(
  inventory: Awaited<ReturnType<typeof cmmdApiClient.getRuntimeInventory>>,
  threadId: ThreadId,
): string | null {
  for (const runtime of inventory.runtimes) {
    const hasThread = runtime.projects.some((project) =>
      project.threads.some((thread) => thread.threadId === threadId),
    );
    if (hasThread) return runtime.runtime.id;
  }
  return null;
}

export async function waitForProjectLeadThreadDurability(input: {
  readonly cmmdProjectId: string;
  readonly threadId: ThreadId;
  readonly readInventory?: typeof cmmdApiClient.getRuntimeInventory;
  readonly wait?: (durationMs: number) => Promise<void>;
  readonly attempts?: number;
}): Promise<void> {
  const projectId = Number(input.cmmdProjectId);
  if (!Number.isSafeInteger(projectId) || projectId <= 0) {
    throw new Error("Forge could not resolve the CMMD project for this conversation.");
  }
  const readInventory =
    input.readInventory ?? cmmdApiClient.getRuntimeInventory.bind(cmmdApiClient);
  const wait =
    input.wait ??
    ((durationMs: number) => new Promise((resolve) => setTimeout(resolve, durationMs)));
  const attempts = input.attempts ?? THREAD_DURABILITY_ATTEMPTS;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      // The CMMD response is already scoped by the stable project id. Its exact
      // thread entry is the durability receipt and names the authoritative
      // replication runtime; a public Forge environment id is not a runtime-id
      // substitute and primary environments need not exist in the saved registry.
      const inventory = await readInventory({ projectId });
      if (findProjectLeadThreadReceipt(inventory, input.threadId) !== null) return;
    } catch {
      // A transient owner-API failure is not a durability receipt. Retry within the bounded wait.
    }
    if (attempt + 1 < attempts) await wait(THREAD_DURABILITY_INTERVAL_MS);
  }
  throw new Error(
    "Forge created the conversation locally but could not confirm the durable Project Lead conversation. Retry to continue safely.",
  );
}

async function readDispatchError(response: Response): Promise<string> {
  const fallback = "Forge could not create the Project Lead conversation.";
  const payload = (await response.json().catch(() => null)) as {
    error?: unknown;
    message?: unknown;
  } | null;
  const message = payload?.error ?? payload?.message;
  return typeof message === "string" && message.trim().length > 0 ? message : fallback;
}

export async function dispatchProjectLeadThreadCreate(input: {
  readonly command: ProjectLeadCreateCommand;
  readonly environmentId: EnvironmentId;
  readonly primaryEnvironmentId: ReturnType<typeof usePrimaryEnvironmentId>;
  readonly readApi?: (environmentId: EnvironmentId) => EnvironmentApi | undefined;
  readonly fetchFn?: typeof fetch;
}): Promise<void> {
  const command: ClientOrchestrationCommand = input.command;
  const api = (input.readApi ?? readEnvironmentApi)(input.environmentId);
  if (api) {
    await api.orchestration.dispatchCommand(command);
    return;
  }
  if (input.environmentId !== input.primaryEnvironmentId) {
    throw new Error("The selected Forge runtime is not connected.");
  }

  const response = await (input.fetchFn ?? fetch)(
    resolvePrimaryEnvironmentHttpUrl("/api/orchestration/dispatch"),
    buildPrimaryFetchInit({
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(command),
    }),
  );
  if (!response.ok) {
    throw new ProjectLeadThreadCreateRejectedError(await readDispatchError(response));
  }
}

export function useProjectLeadTaskCreationThread(): {
  readonly ensureThread: (
    input: EnsureProjectLeadTaskCreationThreadInput,
  ) => Promise<EnsureProjectLeadTaskCreationThreadResult>;
} {
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryProviders = useServerProviders();
  const cmmdIntake = useCmmdWorkIntakeSnapshot();

  const ensureThread = useCallback(
    async (input: EnsureProjectLeadTaskCreationThreadInput) => {
      const projectRef = await ensureProjectLeadTarget({
        projectRef: input.projectRef,
        cmmdProjectId: input.cmmdProjectId,
        primaryEnvironmentId,
        cmmdIntake,
      });
      const result = await ensureProjectLeadTaskCreationThread(
        { ...input, projectRef },
        {
          findThreads: (projectRef) =>
            selectSidebarThreadsForProjectRef(useStore.getState(), projectRef),
          createThreadId: newThreadId,
          createCommandId: newCommandId,
          dispatchCreate: async (command) => {
            await dispatchProjectLeadThreadCreate({
              command,
              environmentId: projectRef.environmentId,
              primaryEnvironmentId,
            });
            await waitForProjectLeadThreadProjection({
              projectRef,
              threadId: command.threadId,
            });
          },
          resolveModelSelection: async (projectRef) => {
            const project = selectProjectByRef(useStore.getState(), projectRef);
            const prepared = await prepareThreadProviderLaunch({
              environmentId: projectRef.environmentId,
              primaryEnvironmentId,
              primaryProviders,
              preferred: project?.defaultModelSelection ?? null,
              pinPreferredProvider:
                project?.defaultModelSelection !== null &&
                project?.defaultModelSelection !== undefined,
            });
            return prepared.launchModel.modelSelection;
          },
          seedComposer: (threadRef, prompt, replaceExistingPrompt) => {
            const composerStore = useComposerDraftStore.getState();
            seedThreadComposerContext({
              target: threadRef,
              prompt,
              existingPrompt: composerStore.getComposerDraft(threadRef)?.prompt,
              replaceExistingPrompt,
              setPrompt: composerStore.setPrompt,
              setPendingAutoSubmit: composerStore.setPendingAutoSubmit,
            });
          },
        },
      );
      await waitForProjectLeadThreadDurability({
        cmmdProjectId: input.cmmdProjectId,
        threadId: result.threadRef.threadId,
      });
      return result;
    },
    [cmmdIntake, primaryEnvironmentId, primaryProviders],
  );

  return { ensureThread };
}
